import {
  Alert,
  Button,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useEffect, useState } from 'react'
import {
  useCompanies,
  useEventTypes,
  useRemoveEventType,
  useSaveEventType,
  useServiceTypes,
} from '@/api/hooks'
import type { EventTypeView } from '@/api/types'
import { useAuthStore } from '@/auth/authStore'
import { ErrorBanner } from '@/components/common'

const BLANK: EventTypeView = {
  id: '',
  serviceType: '',
  code: '',
  name: '',
  sortOrder: 99,
  active: true,
}

/**
 * The occasions each line of business is sold for.
 *
 * <p>Nine of these were written into the enquiry screen and offered whatever
 * was being sold, so a bulk takeaway was asked which wedding it was for and an
 * office whose weddings are only ever muhurthams and receptions could not say
 * so.</p>
 *
 * <p>There is no "ask for the occasion" switch here on purpose. Whether it is
 * asked for is whether this service has any mapped: a service with none is a
 * service that is not asked. A switch as well would let a service demand an
 * occasion while offering nothing to choose from.</p>
 */
export function OccasionsTab() {
  const can = useAuthStore((state) => state.can)
  const canWrite = useAuthStore((state) => state.canWrite)
  const ownCompanyId = useAuthStore((state) => state.user?.companyId)
  const [companyId, setCompanyId] = useState<string | undefined>(undefined)
  const target = companyId ?? ownCompanyId
  const { data: companies } = useCompanies()
  const canPickCompany = can('platform.admin') && (companies ?? []).length > 1

  const { data: services } = useServiceTypes({ companyId, includeInactive: true })
  const { data: occasions, error } = useEventTypes({ companyId, includeInactive: true })
  const save = useSaveEventType()
  const remove = useRemoveEventType()

  const [rows, setRows] = useState<EventTypeView[]>([])
  const writable = canWrite('config')

  useEffect(() => {
    setRows((occasions ?? []).map((row) => ({ ...row })))
  }, [occasions])

  const update = (index: number, patch: Partial<EventTypeView>) =>
    setRows((current) => current.map((row, at) => (at === index ? { ...row, ...patch } : row)))

  // Which services would ask for an occasion as things stand, so the effect of
  // mapping and unmapping is visible without leaving the screen.
  const asking = new Set(rows.filter((row) => row.active).map((row) => row.serviceType))
  const silent = (services ?? []).filter((service) => service.active && !asking.has(service.code))

  return (
    <>
      <ErrorBanner error={error ?? save.error ?? remove.error} />

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          {canPickCompany ? (
            <TextField
              select
              size="small"
              label="Company"
              value={target ?? ''}
              onChange={(event) => setCompanyId(event.target.value)}
              sx={{ minWidth: 220 }}
              inputProps={{ 'aria-label': 'Company' }}
            >
              {(companies ?? []).map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  {company.name}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          <Typography variant="caption" color="text.secondary">
            What each line of business is sold for. A service with none listed is never asked the
            question.
          </Typography>
        </Stack>
        {writable ? (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setRows((current) => [...current, { ...BLANK }])}
          >
            New occasion
          </Button>
        ) : null}
      </Stack>

      {silent.length > 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Not asked what occasion it is for:{' '}
          {silent.map((service) => service.name).join(', ')}. Add one below to start asking.
        </Alert>
      ) : null}

      <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 900 }}>
        <TableHead>
          <TableRow>
            <TableCell width={240}>Line of business</TableCell>
            <TableCell width={200}>Code</TableCell>
            <TableCell width={240}>Called</TableCell>
            <TableCell width={100}>Order</TableCell>
            <TableCell width={90}>Offered</TableCell>
            <TableCell align="right" width={110}>
              &nbsp;
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => {
            // The code is what every enquiry, quotation and order already
            // recorded, so it is fixed once anything has been raised under it.
            const codeFixed = Boolean(row.id)
            return (
              <TableRow key={row.id || `new-${index}`} sx={{ opacity: row.active ? 1 : 0.55 }}>
                <TableCell>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={row.serviceType}
                    disabled={!writable || codeFixed}
                    onChange={(event) => update(index, { serviceType: event.target.value })}
                    inputProps={{ 'aria-label': `Line of business ${index + 1}` }}
                  >
                    {(services ?? []).map((service) => (
                      <MenuItem key={service.code} value={service.code}>
                        {service.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    value={row.code}
                    disabled={!writable || codeFixed}
                    onChange={(event) =>
                      update(index, {
                        code: event.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''),
                      })
                    }
                    inputProps={{ 'aria-label': `Code ${index + 1}` }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    value={row.name}
                    disabled={!writable}
                    onChange={(event) => update(index, { name: event.target.value })}
                    inputProps={{ 'aria-label': `Called ${index + 1}` }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={row.sortOrder}
                    disabled={!writable}
                    onChange={(event) => update(index, { sortOrder: Number(event.target.value) })}
                    inputProps={{ 'aria-label': `Order ${index + 1}` }}
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title="Off means it is no longer offered; work raised under it still names it">
                    <Switch
                      size="small"
                      checked={row.active}
                      disabled={!writable}
                      onChange={(event) => update(index, { active: event.target.checked })}
                      inputProps={{ 'aria-label': `Offered ${index + 1}` }}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  {writable ? (
                    <>
                      <Button
                        size="small"
                        disabled={
                          save.isPending || !row.serviceType.trim() || !row.code.trim() || !row.name.trim()
                        }
                        onClick={() =>
                          save.mutate({
                            id: row.id || undefined,
                            companyId,
                            serviceType: row.serviceType,
                            code: row.code,
                            name: row.name,
                            sortOrder: row.sortOrder,
                            active: row.active,
                          })
                        }
                      >
                        Save
                      </Button>
                      {row.id ? (
                        <Tooltip title="Withdraw">
                          <IconButton
                            size="small"
                            onClick={() => remove.mutate({ id: row.id, companyId })}
                            aria-label={`Withdraw ${row.name}`}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </>
                  ) : null}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </>
  )
}
